import { Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { updateSelection } from 'src/shared/services/selectionService';
import { ArrowForward } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { MemberSearchFormState } from '../models/memberSearchFormState';
import { MemberSelectionChip } from './memberSelectionChip';
import { SelectionPutRequest } from '../models/selectionPutRequest';

type UpdateSavedSelectionProps = {
  modalOpen: boolean;
  setModalOpen: (closeModel: boolean) => void;
  setUpdateSelectionOptions: (updateOptions: boolean) => void;
  currentSelection: MemberSearchFormState;
};

export const UpdateSavedSelection = (props: UpdateSavedSelectionProps) => {
  const { modalOpen, setModalOpen, setUpdateSelectionOptions, currentSelection } = props;
  const { getValues } = useFormContext();
  const updatedValues = getValues() as MemberSearchFormState;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const schema = yup.object().shape({
    sendings: yup.array(
      yup.object().shape({
        selection_id: yup.number().required(),
        selectionName: yup.string().required(),
        selectionInfo: yup.string().required(),
      }),
    ),
  });

  const formMethods = useForm<SelectionPutRequest>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      selection_id: currentSelection.selectionId || undefined,
      selectionName: currentSelection.selectionName || undefined,
      selectionInfo: currentSelection.selectionInfo || undefined,
    },
  });

  const { handleSubmit } = formMethods;

  const closeModal = () => {
    setModalOpen(false);
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function removeEmptyArraysAndNulls(obj: Record<string, any>) {
    if (!obj) return {};
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== undefined && value !== null && !(Array.isArray(value) && value.length === 0)),
    );
  }
  const onHandleSave = async (data: SelectionPutRequest) => {
    setIsLoading(true);

    const converted = {
      ...updatedValues,
      dueDateEnd: updatedValues.dueDateEnd ? DateHelper.getDateTime(updatedValues.dueDateEnd) : '',
      dueDateStart: updatedValues.dueDateStart ? DateHelper.getDateTime(updatedValues.dueDateStart) : '',

      admDateEnd: updatedValues.admDateEnd ? DateHelper.getDateTime(updatedValues.admDateEnd) : '',
      admDateStart: updatedValues.admDateStart ? DateHelper.getDateTime(updatedValues.admDateStart) : '',

      payDateEnd: updatedValues.payDateEnd ? DateHelper.getDateTime(updatedValues.payDateEnd) : '',
      payDateStart: updatedValues.payDateStart ? DateHelper.getDateTime(updatedValues.payDateStart) : '',

      resDateStart: updatedValues.resDateStart ? DateHelper.getDateTime(updatedValues.resDateStart) : '',
      resDateEnd: updatedValues.resDateEnd ? DateHelper.getDateTime(updatedValues.resDateEnd) : '',

      birthDateStart: updatedValues.birthDateStart ? DateHelper.getDateTime(updatedValues.birthDateStart) : '',
      birthDateEnd: updatedValues.birthDateEnd ? DateHelper.getDateTime(updatedValues.birthDateEnd) : '',
    };

    const filterFormValues = removeEmptyArraysAndNulls(converted ?? {});
    const dataToPost: SelectionPutRequest = {
      ...data,
      json: JSON.stringify(filterFormValues),
    };

    try {
      await updateSelection(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('member:selection.updatedSelectionSuccess')}</>);
        setUpdateSelectionOptions(true);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setIsLoading(false);
      closeModal();
    }
  };

  return (
    <GenericDialog
      open={modalOpen}
      onClose={() => closeModal()}
      onSave={handleSubmit(onHandleSave)}
      dialogTitle={t('member:memberAdvancedSearch.updateSelection')}
      dialogContent={
        <Box p={2}>
          <LoadingOverlay loading={isLoading} />
          <form id='update-selection-form' onSubmit={handleSubmit(onHandleSave)}>
            <GridWrapper>
              <Grid item xs={12}>
                <Box display='flex' alignItems='stretch'>
                  <Grid item xs={6}>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                      {t('member:selection:previousSelection')}
                    </Typography>
                    <MemberSelectionChip values={currentSelection} newValues={false} />
                  </Grid>

                  <Divider orientation='vertical' sx={{ mr: 3 }} flexItem>
                    <Box display='flex' alignItems='center' justifyContent='center' height='100%'>
                      <ArrowForward />
                    </Box>
                  </Divider>

                  <Grid item xs={6}>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                      {t('member:selection:updatedSelection')}
                    </Typography>
                    <MemberSelectionChip values={updatedValues} newValues />
                  </Grid>
                </Box>
              </Grid>
            </GridWrapper>
          </form>
        </Box>
      }
      dialogActions={
        <Stack spacing={1} direction='row'>
          <Button size='large' disableElevation variant='outlined' color='primary' fullWidth onClick={() => closeModal()}>
            {t('common:button.cancel')}
          </Button>
          <Button
            form='update-selection-form'
            size='large'
            disabled={isLoading}
            disableElevation
            variant='contained'
            color='primary'
            type='submit'
            fullWidth
          >
            {t('common:button.save')}
          </Button>
        </Stack>
      }
    />
  );
};
